import { type CentraLanguageCode, type LanguageOption } from "@/data/supported-languages"
import { type CentraMarketCode } from "@/data/supported-markets"
import { type CentraPricelistCode } from "@/data/supported-pricelists"
import { type CountryCode } from "@/data/country-data"
import { UNIVERSAL_DEFAULT_LANGUAGE } from "@/lib/siteconfig/constants"
import { isStringEuroCurrencyPreferredApp } from "@/src/utils/strings/isStringEuroCurrencyPreferredApp"
import {
    extractCountrySegmentFromAppLocale,
    getMarketCodeFromAppLocale,
    mapPreferredAppToMarketCodeThenToMarketId,
    mapPreferredAppToPriceListCode,
    mapPreferredAppToPriceListId
} from "./app-locale-checkout-bridge"
import { getLangCode } from "@/src/utils/string/getLangCode"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * Single source of truth for locale → market / pricelist / language / country mapping.
 *
 * Built once at module load from NEXT_APP_CONFIG. There is no hand-maintained
 * data here — adding a locale to NEXT_APP_CONFIG automatically flows through.
 *
 * **Lives in `i18n/` because it is fundamentally i18n-derived data**: the catalog is
 * keyed off NEXT_APP_CONFIG and exists to give i18n routes a richer shape (market,
 * pricelist, language, country, hrefLang). It is not generic library code.
 */
export type AppConfig = {
    readonly locale: string
    readonly path: string
    readonly language: CentraLanguageCode
    readonly market: CentraMarketCode
    readonly marketId: number
    readonly pricelist: CentraPricelistCode
    readonly pricelistId: number
    /**
     * ISO 3166-1 alpha-2 uppercase. `null` for language-only locales (`/en`, `/de`, `/fr`)
     * which serve the EU market without a specific country.
     */
    readonly countryCode: CountryCode | null
    readonly hrefLang: string
    /**
     * `true` while the locale is configured but not public yet. Preview locales are
     * excluded from every public surface (routing gates, redirect targets, site
     * selector, sitemaps) and render only in Sanity draft mode. See `isPreviewLocale`.
     */
    readonly preview: boolean
}

export interface Country {
    value: string
    label: string
    countryCode: string
}

export interface CountryWithLanguages extends Country {
    languages: LanguageOption[]
}

/**
 * A single entry in NEXT_APP_CONFIG. Each `path` inside `languages` must be a
 * valid locale path that can be resolved by `buildApp` (i.e. it must map to a
 * known Centra market, pricelist, and language code).
 */
export type NextAppConfigEntry = CountryWithLanguages

// ---------------------------------------------------------------------------
// NEXT_APP_CONFIG — single source of truth for country + locale combinations
// ---------------------------------------------------------------------------

/**
 * The complete app configuration for every country we ship to.
 *
 * This is the single source of truth for:
 *  - Which shipping countries are supported.
 *  - Which locale path(s) are available per country.
 *
 * `APPS` is derived directly from this list (unique paths). Adding a path here
 * is all that is needed to register a new locale — no secondary list to keep in sync.
 *
 * Path conventions:
 *  - `/{lang}-{country}` for countries with a dedicated URL country segment
 *  - `/{lang}` for EUR-zone countries that share a language-only locale path
 */
export const NEXT_APP_CONFIG: readonly NextAppConfigEntry[] = [
    {
        value: "Australia",
        label: "Australia",
        countryCode: "AU",
        languages: [{ language: "en", label: "English", path: "/en-au" }]
    },
    {
        value: "Austria",
        label: "Austria",
        countryCode: "AT",
        languages: [
            { language: "de", label: "German", path: "/de" },
            { language: "en", label: "English", path: "/en" }
        ]
    },
    {
        value: "Belgium",
        label: "Belgium",
        countryCode: "BE",
        languages: [
            { language: "en", label: "English", path: "/en" },
            { language: "de", label: "German", path: "/de" },
            { language: "fr", label: "French", path: "/fr-be" }
        ]
    },
    {
        value: "Bulgaria",
        label: "Bulgaria",
        countryCode: "BG",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Canada",
        label: "Canada",
        countryCode: "CA",
        languages: [
            { language: "en", label: "English", path: "/en-ca" },
            { language: "fr", label: "French", path: "/fr-ca" }
        ]
    },
    {
        value: "Croatia",
        label: "Croatia",
        countryCode: "HR",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Cyprus",
        label: "Cyprus",
        countryCode: "CY",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Czech Republic",
        label: "Czech Republic",
        countryCode: "CZ",
        languages: [{ language: "en", label: "English", path: "/en-cz" }]
    },
    {
        value: "Denmark",
        label: "Denmark",
        countryCode: "DK",
        languages: [{ language: "en", label: "English", path: "/en-dk" }]
    },
    {
        value: "Estonia",
        label: "Estonia",
        countryCode: "EE",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Finland",
        label: "Finland",
        countryCode: "FI",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "France",
        label: "France",
        countryCode: "FR",
        languages: [
            { language: "fr", label: "French", path: "/fr" },
            { language: "en", label: "English", path: "/en" }
        ]
    },
    {
        value: "Germany",
        label: "Germany",
        countryCode: "DE",
        languages: [
            { language: "de", label: "German", path: "/de" },
            { language: "en", label: "English", path: "/en" }
        ]
    },
    {
        value: "Greece",
        label: "Greece",
        countryCode: "GR",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Hungary",
        label: "Hungary",
        countryCode: "HU",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Ireland",
        label: "Ireland",
        countryCode: "IE",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Italy",
        label: "Italy",
        countryCode: "IT",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Japan",
        label: "Japan",
        countryCode: "JP",
        languages: [
            { language: "ja", label: "Japanese", path: "/ja-jp" },
            { language: "en", label: "English", path: "/en-jp" }
        ]
    },
    {
        value: "Latvia",
        label: "Latvia",
        countryCode: "LV",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Lithuania",
        label: "Lithuania",
        countryCode: "LT",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Luxembourg",
        label: "Luxembourg",
        countryCode: "LU",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Malta",
        label: "Malta",
        countryCode: "MT",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Netherlands",
        label: "Netherlands",
        countryCode: "NL",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "New Zealand",
        label: "New Zealand",
        countryCode: "NZ",
        languages: [{ language: "en", label: "English", path: "/en-nz" }]
    },
    {
        value: "Norway",
        label: "Norway",
        countryCode: "NO",
        languages: [{ language: "en", label: "English", path: "/en-no" }]
    },
    {
        value: "Poland",
        label: "Poland",
        countryCode: "PL",
        languages: [{ language: "en", label: "English", path: "/en-pl" }]
    },
    {
        value: "Portugal",
        label: "Portugal",
        countryCode: "PT",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Romania",
        label: "Romania",
        countryCode: "RO",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Slovakia",
        label: "Slovakia",
        countryCode: "SK",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Slovenia",
        label: "Slovenia",
        countryCode: "SI",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Spain",
        label: "Spain",
        countryCode: "ES",
        languages: [{ language: "en", label: "English", path: "/en" }]
    },
    {
        value: "Sweden",
        label: "Sweden",
        countryCode: "SE",
        languages: [{ language: "en", label: "English", path: "/en-se" }]
    },
    {
        value: "Switzerland",
        label: "Switzerland",
        countryCode: "CH",
        languages: [
            { language: "de", label: "German", path: "/de-ch" },
            { language: "fr", label: "French", path: "/fr-ch" },
            { language: "en", label: "English", path: "/en-ch" }
        ]
    },
    {
        value: "United Kingdom",
        label: "United Kingdom",
        countryCode: "GB",
        languages: [{ language: "en", label: "English", path: "/en-gb" }]
    },
    {
        value: "United States",
        label: "United States",
        countryCode: "US",
        languages: [{ language: "en", label: "English", path: "/en-us" }]
    }
]

// ---------------------------------------------------------------------------
// Preview locales
// ---------------------------------------------------------------------------

/**
 * Collects the locale paths that are still preview-only.
 *
 * A path counts as preview only when *every* declaration of it is marked
 * `preview: true`. Many countries share `/en`, `/de` and `/fr`, so one country
 * opting into a preview locale must never pull an already-live shared path
 * offline for everyone else.
 *
 * Exported for the derivation below and for its own tests — consumers should ask
 * `isPreviewLocale` / `isLiveAppLocale` instead of re-deriving this.
 */
export const derivePreviewLocalePaths = (
    config: readonly NextAppConfigEntry[]
): ReadonlySet<string> => {
    const declarations = new Map<string, boolean>()

    config.forEach(country =>
        country.languages.forEach(language => {
            if (!language.path) return
            const isPreview = language.preview === true
            const alreadySeen = declarations.get(language.path)
            declarations.set(
                language.path,
                alreadySeen === undefined ? isPreview : alreadySeen && isPreview
            )
        })
    )

    return new Set(
        [...declarations].flatMap(([path, isPreview]) => (isPreview ? [path] : []))
    )
}

const previewLocalePaths = derivePreviewLocalePaths(NEXT_APP_CONFIG)

const isPreviewLocalePath = (path: string): boolean => previewLocalePaths.has(path)

/**
 * Countries that share `/en`, `/de` or `/fr`. Same EUR market, but VAT and
 * shipping are still per country.
 */
export const EUR_MARKET_COUNTRY_CODES: ReadonlySet<CountryCode> = new Set(
    NEXT_APP_CONFIG.flatMap(country =>
        country.languages.some(
            language =>
                language.path &&
                !language.path.includes("-") &&
                !isPreviewLocalePath(language.path)
        )
            ? [country.countryCode as CountryCode]
            : []
    )
)

// ---------------------------------------------------------------------------
// APPS — derived from NEXT_APP_CONFIG (unique locale paths)
// ---------------------------------------------------------------------------

const buildApp = (locale: string): AppConfig => {
    const language = getLangCode(locale)
    const marketId = mapPreferredAppToMarketCodeThenToMarketId(locale)
    const market = getMarketCodeFromAppLocale(locale)
    const pricelist = mapPreferredAppToPriceListCode(locale)
    const pricelistId = mapPreferredAppToPriceListId(locale)

    if (!language) {
        throw new Error(
            `i18n/apps: failed to derive language for locale "${locale}". Check getLangCode + NEXT_APP_CONFIG.`
        )
    }
    if (!pricelist) {
        throw new Error(
            `i18n/apps: failed to derive pricelist code for locale "${locale}".`
        )
    }
    if (!pricelistId) {
        throw new Error(
            `i18n/apps: failed to derive pricelist id for locale "${locale}".`
        )
    }
    if (!marketId) {
        throw new Error(`i18n/apps: failed to derive market id for locale "${locale}".`)
    }

    const isLanguageOnly = isStringEuroCurrencyPreferredApp(locale)
    const countryCode: CountryCode | null = isLanguageOnly
        ? null
        : ((locale.split("-")[1]?.toUpperCase() as CountryCode | undefined) ?? null)

    return {
        locale,
        path: `/${locale}`,
        language,
        market,
        marketId,
        pricelist,
        pricelistId,
        countryCode,
        hrefLang: locale,
        preview: isPreviewLocalePath(`/${locale}`)
    }
}

/**
 * Unique locale paths from NEXT_APP_CONFIG. Many EU countries share paths like
 * `/en` or `/de` — we deduplicate so each `AppConfig` is built only once.
 * Insertion order is preserved; first occurrence wins.
 */
const uniqueLocalePaths = [
    ...new Set(NEXT_APP_CONFIG.flatMap(c => c.languages.map(l => l.path!)))
]

/**
 * Full `AppConfig` catalog. Built once at module load from the unique locale
 * paths declared in NEXT_APP_CONFIG. NEXT_APP_CONFIG is the single source of
 * truth — adding a path there is all that is needed to register a new locale.
 */
export const APPS: readonly AppConfig[] = uniqueLocalePaths.map(path =>
    buildApp(path.slice(1))
)

// Fast-lookup maps derived from APPS
const localeToApp = new Map<string, AppConfig>(APPS.map(app => [app.locale, app]))
const countryCodeToApp = new Map<CountryCode, AppConfig>(
    APPS.flatMap(app => (app.countryCode ? [[app.countryCode, app] as const] : []))
)

/**
 * Every app that is public. Preview locales are excluded.
 *
 * Use this — never `APPS` — for anything a visitor or a crawler can reach:
 * routing gates, redirect targets, the site selector, sitemaps, hreflangs.
 */
export const LIVE_APPS: readonly AppConfig[] = APPS.filter(app => !app.preview)

/** Locale strings of every public app. The list sitemaps and hreflangs must build from. */
export const liveLocales: readonly string[] = LIVE_APPS.map(app => app.locale)

// ---------------------------------------------------------------------------
// Public utilities
// ---------------------------------------------------------------------------

/**
 * Whether the locale is configured but not public yet (`preview: true` in
 * NEXT_APP_CONFIG). Accepts both `"en-se"` and `"/en-se"`.
 *
 * An unknown locale is not a preview locale — it is simply unknown, so this
 * returns `false`. Gate on `isLiveAppLocale` when you need "public and real".
 */
export const isPreviewLocale = (locale: string | null | undefined): boolean => {
    if (!locale) return false
    return localeToApp.get(locale.replace(/^\/+/, "").toLowerCase())?.preview === true
}

/**
 * Whether the locale is both configured and public — the check a public-facing
 * gate wants. Unknown locales and preview locales both return `false`.
 */
export const isLiveAppLocale = (locale: string | null | undefined): boolean => {
    if (!locale) return false
    return localeToApp.get(locale.replace(/^\/+/, "").toLowerCase())?.preview === false
}

/**
 * Resolves the best matching app for a given locale string.
 *
 * Accepts both the bare locale (`"en-se"`) and the path form (`"/en-se"`) —
 * `preferredApp` in the cookie is always stored with a leading slash (sourced
 * from `app.path`), and callers should not have to strip it before calling here.
 *
 * Lookup order:
 * 1. Exact match (e.g. `"en-se"` → `/en-se`).
 * 2. Language-only fallback (e.g. `"en-xx"` → `/en` if no country match).
 * 3. `UNIVERSAL_DEFAULT_LANGUAGE` (`en`) as the final fallback.
 */
export const resolveApp = (locale: string): AppConfig => {
    if (!locale) return localeToApp.get(UNIVERSAL_DEFAULT_LANGUAGE)!
    const normalized = locale.replace(/^\//, "").toLowerCase()

    const exact = localeToApp.get(normalized)
    if (exact) return exact

    const [languagePart] = normalized.split("-")
    if (languagePart) {
        const languageOnly = localeToApp.get(languagePart)
        if (languageOnly) return languageOnly
    }

    return localeToApp.get(UNIVERSAL_DEFAULT_LANGUAGE)!
}

/**
 * Returns the app whose `countryCode` matches the given ISO country code, or `null` if
 * no app serves that country. Language-only apps (countryCode === null) never match.
 *
 * Language-only EU countries (DE, AT, BE, …) never match here — their apps have
 * `countryCode: null`. For geo-IP → locale decisions covering those countries,
 * use `findDefaultPathByCountryCode` (primary path) or `isCountryServedByLocale`
 * (any legitimate pairing).
 */
export const findAppByCountryCode = (
    countryCode: string | null | undefined
): AppConfig | null => {
    if (!countryCode) return null
    return countryCodeToApp.get(countryCode.toUpperCase() as CountryCode) ?? null
}

/**
 * Stable list of valid `path` strings for matching the leading segment of a URL.
 * Sorted longest-first so multi-segment paths (`/en-se`) match before
 * single-segment paths (`/en`).
 */
const byLongestFirst = (paths: string[]): string[] =>
    paths.sort((a, b) => b.length - a.length)

const sortedAppPaths = byLongestFirst(APPS.map(app => app.path))
const sortedLiveAppPaths = byLongestFirst(LIVE_APPS.map(app => app.path))

/**
 * Returns the validated locale segment from a pathname if it matches a real app,
 * otherwise returns `null` — including for locale-shaped-but-invalid segments
 * (e.g. `/en-bg/foo` returns `null`). The proxy treats this as "no URL locale"
 * for purposes of the four-quadrant redirect matrix.
 *
 * **Preview locales are invisible by default.** Returning `null` for them is what
 * gives a preview locale the exact behaviour of a locale that was never configured:
 * both proxy branches then redirect the visitor to their geo/cookie locale, the same
 * way `/en-bg/foo` does today. Pass `includePreview: true` for draft-mode requests,
 * where the preview locale must resolve so the page can render.
 */
export const extractUrlLocale = (
    pathname: string,
    { includePreview = false }: { includePreview?: boolean } = {}
): string | null => {
    if (!pathname) return null
    const candidatePaths = includePreview ? sortedAppPaths : sortedLiveAppPaths
    const matchedPath = candidatePaths.find(
        path => pathname === path || pathname.startsWith(`${path}/`)
    )
    return matchedPath ? matchedPath.slice(1) : null
}

/**
 * Strips the leading locale-shaped segment from a pathname, regardless of whether
 * the locale is valid (in `APPS`) or just locale-shaped (`xx` or `xx-yy`). This
 * guarantees that redirects compose to a single, valid path:
 *
 * - `/en-se/foo`  → `/foo`
 * - `/en-bg/foo`  → `/foo`  (invalid `xx-yy` locale stripped, prevents `/en-se/en-bg/foo`)
 * - `/xx/foo`     → `/foo`  (invalid bare locale stripped, prevents `/en/xx/foo`)
 * - `/en/foo`     → `/foo`  (language-only locale)
 * - `/foo/bar`    → `/foo/bar`  (no leading locale — `foo` is not locale-shaped)
 * - `/`           → `/`
 *
 * Shape, not validity, is the right tool here: invalid locales (typos) are not in `APPS`
 * but must still be stripped so they don't double-stack. The set of valid locales is finite
 * (use `extractUrlLocale` for that); the set of locale-shaped strings is open-ended.
 */
export const stripLeadingLocale = (pathname: string): string => {
    if (!pathname || pathname === "/") return "/"
    // Matches a bare language ("xx") or language-country ("xx-yy") leading segment.
    const localePattern = /^[a-z]{2}(-[A-Za-z]{2})?$/
    const pathSegments = pathname.split("/")
    if (
        (pathSegments.length > 1 && localePattern.test(pathSegments[1])) ||
        isStringEuroCurrencyPreferredApp(pathSegments[1])
    ) {
        return `/${pathSegments.slice(2).join("/")}`
    }
    return pathname
}

/**
 * Composes a redirect target by replacing (or prepending) the locale segment of
 * `currentPathname` with `targetLocalePath`. Always produces a single, well-formed
 * URL — never double-stacks locales.
 *
 * - `buildRedirectTarget("/en-se", "/en-de/foo")` → `"/en-se/foo"`
 * - `buildRedirectTarget("/en-se", "/foo/bar")`   → `"/en-se/foo/bar"`
 * - `buildRedirectTarget("/en-se", "/en-bg/foo")` → `"/en-se/foo"` (invalid stripped)
 * - `buildRedirectTarget("/en-se", "/")`          → `"/en-se"`
 */
export const buildRedirectTarget = (
    targetLocalePath: string,
    currentPathname: string
): string => {
    const suffix = stripLeadingLocale(currentPathname)
    if (suffix === "/" || suffix === "") return targetLocalePath
    return `${targetLocalePath}${suffix}`
}

export const getPreferredAppPathByCountryCode = (
    countryCode: string,
    language?: string
): string | null => {
    if (!countryCode) return null
    const upper = countryCode.toUpperCase()
    const entry = NEXT_APP_CONFIG.find(c => c.countryCode.toUpperCase() === upper)
    if (!entry) return null

    // Preview paths are excluded: this value is written into `basket_session.preferredApp`,
    // and a preview preferredApp would fight the proxy's preview gate on every request.
    const langs = entry.languages.filter(l => l.path && !isPreviewLocalePath(l.path))
    const pick = language
        ? (langs.find(l => l.language === language) ?? langs[0])
        : langs[0]
    const path = pick?.path
    if (!path) return null
    return path.replace(/^\//, "")
}

/**
 * Derives the URL country segment for a shipping country using the first configured locale
 * path in `NEXT_APP_CONFIG` (same convention as historical `LOCALE_PATH_*` maps).
 */
export const getLocalePathCountrySegmentForSiteCountry = (
    isoCountryLower: string
): string => {
    const entry = NEXT_APP_CONFIG.find(
        c => c.countryCode.toLowerCase() === isoCountryLower.toLowerCase()
    )
    const firstPath = entry?.languages[0]?.path
    if (!firstPath) return ""
    return extractCountrySegmentFromAppLocale(firstPath.slice(1))
}

/**
 * Returns `true` if the given locale string (with or without a leading slash)
 * corresponds to a valid locale path in NEXT_APP_CONFIG.
 *
 * Examples: "en-se" → true, "/en-se" → true, "bg-en" → false.
 */
export const validateAppLocale = (locale: string): boolean => {
    const normalized = locale.replace(/^\/+/, "").toLowerCase()
    return localeToApp.has(normalized)
}

// ---------------------------------------------------------------------------
// Country → default path lookup (used by proxy geo-IP redirect)
// ---------------------------------------------------------------------------

/**
 * Map from uppercase ISO country code to the primary locale path for that country.
 * Built from NEXT_APP_CONFIG — covers ALL 34 shipping countries, including those
 * whose primary locale is a language-only path (e.g. Germany → "/de", Austria → "/de").
 *
 * Preview paths are skipped, so a country whose only locale is still preview falls back
 * to `/en` — the same target it resolved to before that locale was ever configured.
 *
 * Unlike `countryCodeToApp` (which returns null for language-only EU countries),
 * this always resolves to a path. Used by the proxy for geo-IP redirect decisions.
 */
const countryToDefaultPath = new Map<string, string>(
    NEXT_APP_CONFIG.map(c => [
        c.countryCode.toUpperCase(),
        c.languages.find(l => !!l.path && !isPreviewLocalePath(l.path))?.path ??
            `/${UNIVERSAL_DEFAULT_LANGUAGE}`
    ])
)

/**
 * Returns the primary locale path for a shipping country from NEXT_APP_CONFIG,
 * or `null` if the country is not in the config.
 *
 * Key difference from `findAppByCountryCode`: this covers language-only EU
 * countries — e.g. "DE" → "/de", "AT" → "/de", "BE" → "/en".
 * Use this in the proxy for geo-IP → locale redirect decisions.
 * Use `findAppByCountryCode` when you need the full AppConfig (market, pricelist, etc.).
 */
export const findDefaultPathByCountryCode = (
    countryCode: string | null | undefined
): string | null => {
    if (!countryCode) return null
    return countryToDefaultPath.get(countryCode.toUpperCase()) ?? null
}

/**
 * Every locale path that serves a given country, keyed by uppercase ISO country code.
 * Germany lists both `/de` and `/en`; Switzerland lists `/de-ch`, `/fr-ch` and `/en-ch`.
 */
const countryToServedPaths = new Map<string, ReadonlySet<string>>(
    NEXT_APP_CONFIG.map(entry => [
        entry.countryCode.toUpperCase(),
        new Set(entry.languages.map(l => l.path).filter((p): p is string => !!p))
    ])
)

/** True if this locale is one of the paths that country is sold on. */
export const isCountryServedByLocale = (
    countryCode: string | null | undefined,
    localePath: string | null | undefined
): boolean => {
    if (!countryCode || !localePath) return false

    const served = countryToServedPaths.get(countryCode.toUpperCase())
    if (!served) return false

    return served.has(localePath.startsWith("/") ? localePath : `/${localePath}`)
}

/** App for a shipping country, including shared locales like `/en` and `/de`. */
export const findAppForShippingCountry = (
    countryCode: string | null | undefined
): AppConfig | null => {
    const exact = findAppByCountryCode(countryCode)
    if (exact) return exact

    const defaultPath = findDefaultPathByCountryCode(countryCode)
    return defaultPath ? resolveApp(defaultPath) : null
}

export {
    extractCountrySegmentFromAppLocale,
    getMarketCodeFromAppLocale,
    mapPreferredAppToPriceListCode,
    mapPreferredAppToPriceListId,
    type LocalePathCountrySegment
} from "./app-locale-checkout-bridge"
